import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ShoppingBag } from 'lucide-react';
import { createPayment, updatePayment, deletePayment, PAY_TYPE_OPTIONS, PAY_METHOD_OPTIONS, TIPOS_COMUNES, TIPOS_COLS, getTipoCambio, putTipoCambio, pesosDeDolares } from '../api';
import { api } from '../../../services/apiClient';
import { listMembers, getMember, getDiscounts, discountLabel, listActiveClasses, trialCheck, linkFamily, MemberRow, Discount, ActiveClass, TrialCheck, PosibleFamiliar } from '../../members/api';
import { putSetting } from '../../../services/settings';
import PersonAvatar from '../../../components/PersonAvatar';
import MetodoPago from './MetodoPago';
import { VentaVitrinaModal } from './CashboxPanel';
import { useToast } from '../../../context/ToastContext';
import ConceptoSelect from './ConceptoSelect';
import PlanClasesAviso, { PlanCheck } from '../../members/components/PlanClasesAviso';

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function monthLabelEs(m: string) {
  const s = new Date(m + '-01T12:00:00').toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}
/** Estado mes a mes de la alumna (API `meses`): alimenta el selector de «Meses» de la mensualidad. */
interface MesEstado { month: string; status: 'pagado' | 'parcial' | 'debe' | 'libre' | 'sin_cargo'; nota: string | null;
  paid: number; expected: number; remaining: number; futuro: boolean; actual: boolean }
function mesCorto(m: string) {
  return new Date(m + '-01T12:00:00').toLocaleDateString('es-MX', { month: 'short' }).replace('.', '');
}
function mesesSeguidos(desde: string, n: number) {
  const out = [desde];
  while (out.length < n) out.push(nextMonth(out[out.length - 1]));
  return out;
}
function textoMes(e: MesEstado) {
  const nm = monthLabelEs(e.month);
  const $ = (n: number) => '$' + n.toLocaleString('es-MX');
  switch (e.status) {
    case 'pagado': return `${nm} — ✓ saldado${e.nota ? ` (${e.nota})` : ''}`;
    case 'parcial': return `${nm} — abonó ${$(e.paid)}, faltan ${$(e.remaining)}`;
    case 'debe': return `${nm} — debe ${$(e.remaining)}`;
    case 'libre': return `${nm} — adelanto`;
    default: return `${nm} — sin cargo`;
  }
}
function nextMonth(m: string) {
  const [y, mm] = m.split('-').map(Number);
  const d = new Date(y, mm, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

/** F2: contexto de "Abonar" a un adeudo existente (desde la pestaña Adeudos).
 *  debt_type/month (29-jul): el pago hereda el tipo y el mes del adeudo, así el renglón del
 *  historial queda como en Kydemy («Licencia / matrícula · AGO 2026») y no como «Otro pago». */
export interface AbonoTarget { debt_id: number; remaining: number; concept: string; member: MemberRow | null; debt_type?: string | null; month?: string | null }

/** Pago existente a corregir (Karla 29-jul: editar/eliminar por renglón, como en Kydemy). */
export interface PaymentEdit {
  payment_id: number; type: string; method: string; amount: string | number;
  pay_date: string | null; months: string | null; assoc_name: string | null; comment: string | null;
  /** Cobro en dólares (1-oct-2026): se corrige por sus USD con SU tipo de cambio. */
  currency?: string | null; usd_amount?: string | number | null; fx_rate?: string | number | null;
  /** Cobro MIXTO (3-nov-2026): parte en pesos de un cobro en dólares. */
  mxn_amount?: string | number | null;
}

/** Cuadritos de opción (tiles) — Karla los prefiere al desplegable. Lo reusa DebtForm. */
/** `conIcono` = son MÉTODOS de pago: se les pone su icono (billete / banco / tarjeta), que es
 *  como Karla los reconoce de un vistazo. Para los tipos de concepto no aplica. */
export function Tiles({ value, options, onPick, cols, conIcono = false }: {
  value: string; options: { v: string; label: string }[]; onPick: (v: string) => void;
  cols: string; conIcono?: boolean;
}) {
  return (
    <div className={`grid gap-2 ${cols}`}>
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => onPick(o.v)}
          className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold leading-tight transition [&_svg]:text-current ${
            value === o.v
              ? 'border-accent-solid bg-accent-solid/15 text-accent-ink'
              : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
          {conIcono && <MetodoPago metodo={o.v} size={15} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Modal para registrar un pago manual. onCreated(pay_date) refresca la vista del día.
 *  Con `edit` el mismo modal corrige un pago ya capturado (PATCH) y permite eliminarlo. */
export default function PaymentForm({ onClose, onCreated, prefill, abono, edit }: { onClose: () => void; onCreated: (payDate: string) => void; prefill?: MemberRow | null; abono?: AbonoTarget | null; edit?: PaymentEdit | null }) {
  const toast = useToast();

  // Alumno (autocomplete) — si viene prefill (desde la ficha) o abono, arranca seleccionado.
  const [picked, setPicked] = useState<MemberRow | null>(abono?.member ?? prefill ?? null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<MemberRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [openList, setOpenList] = useState(false);

  // Al abonar, el tipo lo dicta el adeudo (si es uno de los capturables); si no, 'other'.
  const tipoDelAbono = abono?.debt_type && PAY_TYPE_OPTIONS.some((o) => o.v === abono.debt_type) ? String(abono.debt_type) : 'other';

  // Campos del pago
  const [type, setType] = useState(edit ? edit.type : abono ? tipoDelAbono : 'membership');
  const [method, setMethod] = useState(edit ? edit.method : 'cash');
  // ── DÓLARES (1-oct-2026, audio de Mabel): «ponemos cuántos dólares son y ya nos lo cambia a pesos». El cuadrito
  // «Dólares» = efectivo en dólares; el monto en pesos sale de USD × tipo de cambio (el servidor lo recalcula).
  const usdFijo = !!edit && String(edit.currency || '') === 'USD';   // corregir: con el TC de ESE cobro
  const [enDolares, setEnDolares] = useState(usdFijo);
  const [usd, setUsd] = useState(usdFijo && edit?.usd_amount != null ? String(Number(edit.usd_amount)) : '');
  const [tc, setTc] = useState<number>(usdFijo && edit?.fx_rate ? Number(edit.fx_rate) : 17);
  // COBRO MIXTO (3-nov-2026, Aracely: Ricardo pagó 50 USD + 100 pesos): pesos en efectivo dentro del mismo cobro.
  const [mxn, setMxn] = useState(usdFijo && Number(edit?.mxn_amount) > 0 ? String(Number(edit!.mxn_amount)) : '');
  const mxnNum = Number(mxn) > 0 ? Math.round(Number(mxn) * 100) / 100 : 0;
  const [puedeTc, setPuedeTc] = useState(false);   // cambiar el TC: Karla/Mabel (finanzas), no las cajeras
  const [editTc, setEditTc] = useState<string | null>(null);
  useEffect(() => {
    if (!usdFijo) getTipoCambio().then(setTc).catch(() => {});
    api.get<{ platform?: boolean; modules?: { key: string; level?: string }[] }>('/me/permissions')
      .then((r) => setPuedeTc(!!r.platform || (r.modules || []).find((m) => m.key === 'letsmove_finances')?.level === 'manage'))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (enDolares) setAmount(Number(usd) > 0 ? String(pesosDeDolares(Number(usd), tc) + mxnNum) : ''); }, [enDolares, usd, tc, mxnNum]);
  async function guardarTc() {
    const n = Number(editTc);
    if (!(n >= 5 && n <= 50)) { toast.error('El tipo de cambio debe ir de 5 a 50'); return; }
    try { await putTipoCambio(n); setTc(Math.round(n * 100) / 100); setEditTc(null); toast.success(`Tipo de cambio: ${n} (para todos los cobros y la caja)`); }
    catch (e: any) { toast.error(e?.message || 'No se pudo guardar el tipo de cambio'); }
  }
  const [amount, setAmount] = useState(edit ? String(edit.amount) : abono ? String(abono.remaining) : '');
  const [payDate, setPayDate] = useState(edit ? String(edit.pay_date || '').slice(0, 10) || todayLocal() : todayLocal());
  const [assoc, setAssoc] = useState(edit ? edit.assoc_name || '' : abono ? abono.concept : '');
  // Cobro NUEVO de mensualidad arranca con el mes en curso (1-oct-2026): una mensualidad sin mes no la
  // cuenta Cobranza en ningún mes ni la ve el generador del mes siguiente (le pasó a María Elena Fong).
  const [months, setMonths] = useState(edit ? edit.months || '' : abono?.month ? String(abono.month).slice(0, 7) : todayLocal().slice(0, 7));
  const [comment, setComment] = useState(edit ? edit.comment || '' : '');
  const [saving, setSaving] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [vitrinaOpen, setVitrinaOpen] = useState(false);

  // DESCUENTO DEL COBRO (catálogo de Membresías → Descuentos). Sin esto, un cobro con descuento no
  // deja rastro y el mes se ve incompleto sin explicación — lo que pasó con todo julio-2026.
  const [discountId, setDiscountId] = useState<string>('');
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  // Se guardan TODOS (también archivados): el archivado solo se ofrece si es el de la alumna elegida.
  useEffect(() => { getDiscounts().then((r) => setDiscounts(r.data)).catch(() => {}); }, []);
  const [descAlumna, setDescAlumna] = useState<number | null>(null);   // descuento asignado en su ficha
  // Beca del alumno (7-oct-2026): la inscripción sale con la beca aplicada y la mensualidad avisa.
  const [beca, setBeca] = useState<null | { pct: number; end_date: string; vigente?: boolean; covers_membership: boolean; covers_registration: boolean }>(null);
  const [becaInscPct, setBecaInscPct] = useState(0);
  // Catálogo (7-oct-2026): en un cobro «Otro», en qué renglón de ingresos va (clase privada, renta…).
  const [ingresoConcepto, setIngresoConcepto] = useState<number | null>(null);
  const becaInscRef = useRef(0);
  const opcionesDesc = discounts.filter((d) => !d.deleted_at || d.discount_id === descAlumna);
  // POSIBLE FAMILIA (23-sep-2026): si comparte teléfono o los dos apellidos con otra alumna activa, se
  // SUGIEREN los descuentos de familia arriba — con un clic, nunca solos (4 de 7 familias pagan completo).
  const [familia, setFamilia] = useState<PosibleFamiliar[]>([]);
  const [familiaConf, setFamiliaConf] = useState<{ contact_id: number; nombre: string; activa: boolean }[]>([]);
  // Al aplicar un descuento de familia a una POSIBLE familia, se registra el vínculo cuando se guarda el pago.
  const [vincularCon, setVincularCon] = useState<number[]>([]);
  const [precioLista, setPrecioLista] = useState<number | null>(null);
  // 4-nov-2026 (Mabel): lo que la alumna YA abonó al mes sugerido, para que al cambiar el descuento se reste.
  const [abonadoMes, setAbonadoMes] = useState(0);
  // Meses de la alumna (6 atrás · actual · 3 adelante) y cuántos cubre este pago (curso = varios).
  const [mesesAlumna, setMesesAlumna] = useState<MesEstado[]>([]);
  const [nMeses, setNMeses] = useState(1);
  const descFamilia = opcionesDesc.filter((d) => Number(d.is_family));
  const descFamiliaPropio = descFamilia.find((d) => d.discount_id === descAlumna) || null;
  /** 4-nov-2026 (Mabel, «no ajusta el monto»): elegir un descuento en una MENSUALIDAD recalcula el monto desde el
   *  precio de lista (× meses, menos lo ya abonado al mes). Antes el selector solo explicaba el monto escrito.
   *  «Sin descuento» vuelve al precio de lista; un descuento «incluido en el precio» no resta nada. */
  function elegirDescuento(id: string) {
    setDiscountId(id);
    const d = discounts.find((x) => String(x.discount_id) === id);
    const base = baseMesPara(d);
    if (base == null) return;
    const v = Number(d?.value || 0);
    const neto = !d || Number(d.included_in_price) ? base
      : d.is_percentage ? base * (1 - v / 100) : Math.max(0, base - v);
    const total = Math.max(0, neto - abonadoMes);
    setAmount(String(Math.round(total * 100) / 100));
  }
  /** Precio del mes COMPLETO (× meses) sobre el que se calcula un descuento del cobro, igual que el servidor:
   *  lista; si la alumna tiene descuento vigente en su ficha y se elige OTRO, parte de su precio ya rebajado. */
  function baseMesPara(d: Discount | undefined): number | null {
    if (type !== 'membership' || edit || abono || !precioLista || precioLista <= 0) return null;
    let p = precioLista;
    const fid = mensSug.current?.discountId || '';
    const f = fid ? discounts.find((x) => String(x.discount_id) === fid) : undefined;
    if (f && d && String(d.discount_id) !== fid && !Number(f.included_in_price)) {
      p = f.is_percentage ? p * (1 - Number(f.value) / 100) : Math.max(0, p - Number(f.value));
    }
    return Math.round(p * Math.max(1, nMeses) * 100) / 100;
  }
  /** Aplica un descuento de familia: lo selecciona y ajusta el monto desde el precio de lista. */
  function aplicarDescFamilia(d: Discount) {
    elegirDescuento(String(d.discount_id));
    setVincularCon(familia.map((f) => f.contact_id));
  }

  // Sugerencia automática a partir del plan del alumno (disciplinas + costo + descuento).
  const [suggest, setSuggest] = useState<null | { plan: string; disc: string; monthLabel: string; amount: number; alreadyPaid: boolean }>(null);
  // 25-sep-2026: ¿su plan coincide con las clases inscritas? (aviso, no cambia nada)
  const [planCheck, setPlanCheck] = useState<PlanCheck | null>(null);

  const boxRef = useRef<HTMLDivElement>(null);

  // ===== CLASE SUELTA / MUESTRA (regla de Karla, 15-sep-2026) =====
  // Externo → siempre paga (entre semana / sábado). Alumno con estilo NO inscrito y sin muestra previa
  // → muestra GRATIS ($0). Es un aviso con el monto sugerido; recepción puede cambiarlo.
  const esClase = type === 'voucher_trial' || type === 'class';
  const tipoRef = useRef(type);
  tipoRef.current = type;
  // Sugerencia de MENSUALIDAD de la alumna elegida, guardada aunque ahora se esté cobrando otra cosa:
  // así, al volver a «Mensualidad», regresan su monto, su mes y su plan (Carlos 23-sep: «al dar clic
  // en mensualidad después de clase suelta se queda el precio anterior»).
  const mensSug = useRef<null | { amount: number; months: string; assoc: string; discountId: string; suggest: NonNullable<typeof suggest> }>(null);
  // 🪤 4-nov-2026 (Mabel 16:50, pantalla negra al elegir un descuento): este bloque corre en el RENDER y baseMesPara
  // lee mensSug → tiene que ir DESPUÉS de declarar mensSug (si no: «Cannot access 'X' before initialization»).
  const descElegido = discounts.find((d) => String(d.discount_id) === discountId);
  // Lo que se descuenta se calcula igual que en el servidor: del NETO hacia atrás si es porcentual.
  // 4-nov-2026: en una mensualidad el descuento se calcula sobre el mes COMPLETO (si ya abonó $100 y paga $575
  // con −25 %, el descuento son $225 de $900, no $191.67 de $766.67); en lo demás, del neto hacia atrás.
  const baseDesc = descElegido ? baseMesPara(descElegido) : null;
  const descImporte = descElegido
    ? (baseDesc != null
        ? (descElegido.is_percentage ? Math.round(baseDesc * Number(descElegido.value)) / 100 : Math.min(Number(descElegido.value), baseDesc))
        : descElegido.is_percentage
          ? Math.round((Number(amount) * Number(descElegido.value) / (100 - Number(descElegido.value))) * 100) / 100
          : Number(descElegido.value))
    : 0;
  const precioSinDesc = baseDesc != null ? baseDesc : Number(amount) + descImporte;
  /**
   * Cambiar de tipo desde los cuadritos: el monto va LIGADO al tipo. Cada uno trae el suyo
   * (mensualidad de la alumna · clase suelta del día · inscripción habitual) y los que no tienen
   * precio fijo (evento, vitrina, otro…) quedan en blanco en vez de arrastrar el del tipo anterior.
   */
  /** Selector de meses: al cambiar el mes o cuántos, el monto sigue a lo que falta de cada mes. */
  function elegirMeses(desde: string, n: number) {
    const lista = mesesSeguidos(desde, n);
    setMonths(lista.join(','));
    setNMeses(n);
    const base = Number(mensSug.current?.amount) || 0;
    const precio = mesesAlumna.find((e) => e.status === 'libre')?.expected || base;
    const total = lista.reduce((t, m) => {
      const e = mesesAlumna.find((x) => x.month === m);
      if (!e) return t + precio;
      return t + (e.remaining > 0 ? e.remaining : e.status === 'pagado' ? 0 : e.expected);
    }, 0);
    if (total > 0) setAmount(String(Math.round(total * 100) / 100));
  }

  function elegirTipo(v: string) {
    setType(v);
    if (edit || abono || v === type) return;
    if (v === 'membership') {
      const m = mensSug.current;
      setAmount(m ? String(m.amount) : ''); setMonths(m ? m.months : todayLocal().slice(0, 7)); setAssoc(m ? m.assoc : ''); setNMeses(1);
      setSuggest(m ? m.suggest : null); setDiscountId(m ? m.discountId : '');
      return;
    }
    // El descuento de la alumna es de su MENSUALIDAD: no se arrastra a una clase suelta ni a otro cobro.
    setMonths(''); setAssoc(''); setSuggest(null); setDiscountId('');
    // Vitrina: se cobra con el carrito de la Caja (desglose por artículo), no con monto a mano.
    if (v === 'product') { setAmount(''); setVitrinaOpen(true); return; }
    if (v === 'class' || v === 'voucher_trial') return;          // el precio lo pone trial-check (efecto de abajo)
    // Inscripción: $200 en 164 de 169 cobros desde jun-2026 (medido 23-sep). Es sugerencia, se edita.
    // Beca que cubre inscripción: se aplica el % (completa = $0). Sigue siendo editable.
    setAmount(v === 'license' ? String(Math.round(200 * (1 - becaInscRef.current / 100) * 100) / 100) : '');
  }
  const [clases, setClases] = useState<ActiveClass[]>([]);
  const [claseId, setClaseId] = useState<number | null>(null);
  const [check, setCheck] = useState<TrialCheck | null>(null);
  const [editPrices, setEditPrices] = useState<null | { weekday: string; saturday: string }>(null);
  useEffect(() => {
    if (!esClase || edit || abono || clases.length) return;
    listActiveClasses({ vigentes: true }).then((r) => setClases(r.data)).catch(() => {});
  }, [esClase, edit, abono, clases.length]);
  useEffect(() => {
    if (!esClase || edit || abono) { setCheck(null); return; }
    let cancel = false;
    trialCheck({ class_id: claseId, contact_id: picked?.account_id ?? null, date: payDate })
      .then((c) => {
        if (cancel) return;
        setCheck(c);
        // 23-sep-2026 (Carlos): antes el monto solo se ponía al elegir la clase, y mientras tanto se
        // quedaba la MENSUALIDAD que se sugirió al elegir a la alumna ($700 en una clase suelta).
        // Ahora el precio del día (entre semana / sábado) entra en cuanto el tipo es clase.
        setAmount(String(c.charge));
        if (claseId) {
          setAssoc(c.class_name || '');
          setType(c.free_available ? 'voucher_trial' : 'class');
        }
      }).catch(() => setCheck(null));
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esClase, claseId, picked?.account_id, payDate]);
  async function guardarPrecios() {
    if (!editPrices) return;
    const w = Number(editPrices.weekday), sa = Number(editPrices.saturday);
    if (!(w >= 0) || !(sa >= 0)) { toast.error('Precios inválidos'); return; }
    try {
      await putSetting('trial_prices', JSON.stringify({ weekday: w, saturday: sa }));
      toast.success('Precios de clase suelta guardados');
      setEditPrices(null);
      setCheck(null); setClaseId((c) => c); // fuerza recomputar
      const c = await trialCheck({ class_id: claseId, contact_id: picked?.account_id ?? null, date: payDate });
      setCheck(c); if (claseId) setAmount(String(c.charge));
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
  }

  // Al elegir alumno: traer su plan/descuento/estado del mes y prellenar el formulario.
  // En modo ABONO no aplica (pisaría el monto/concepto del adeudo).
  useEffect(() => {
    // En EDICIÓN tampoco: pisaría con la sugerencia lo que se está corrigiendo.
    if (abono || edit || !picked?.account_id) { setSuggest(null); setPlanCheck(null); return; }
    setBeca(null); setBecaInscPct(0); becaInscRef.current = 0;
    mensSug.current = null; setDescAlumna(null); setFamilia([]); setFamiliaConf([]); setVincularCon([]); setPrecioLista(null); setMesesAlumna([]); setNMeses(1);
    let cancel = false;
    getMember(picked.account_id).then((r) => {
      if (cancel) return;
      const mm = r.data;
      setPlanCheck(mm.plan_check || null);
      setBeca(mm.beca || null);
      const bi = Number(mm.beca_inscripcion_pct || 0);
      setBecaInscPct(bi); becaInscRef.current = bi;
      if (bi > 0 && tipoRef.current === 'license') setAmount(String(Math.round(200 * (1 - bi / 100) * 100) / 100));
      const eff = Number(mm.effective_month || 0);
      // Sin plan (p. ej. prospecto recién convertido): no hay sugerencia, pero el mes no se queda vacío.
      if (!(eff > 0)) { setSuggest(null); if (tipoRef.current === 'membership') setMonths((m) => m || todayLocal().slice(0, 7)); return; }
      const pm = mm.pay_month as null | { month: string; status: string; remaining: number };
      const alreadyPaid = !!pm && pm.status === 'paid';
      // Mes en hora LOCAL (todayLocal ya existe arriba): en UTC-7 el ISO adelanta el mes a partir de las 17:00.
      // Si ya pagó el mes, se sugiere el primer mes SIGUIENTE que no esté pagado: con curso prepagado hasta
      // diciembre (Sara Gomez), sugerir octubre era cobrar un mes que ya pagó.
      const mesesA: MesEstado[] = Array.isArray(mm.meses) ? mm.meses : [];
      const libreSig = alreadyPaid ? mesesA.find((e) => e.month > pm!.month && e.status !== 'pagado' && e.status !== 'sin_cargo') : undefined;
      const ultimoPagado = alreadyPaid && !libreSig && mesesA.length && mesesA[mesesA.length - 1].status === 'pagado' ? mesesA[mesesA.length - 1].month : null;
      const month = alreadyPaid ? (libreSig ? libreSig.month : nextMonth(ultimoPagado || pm!.month)) : (pm ? pm.month : todayLocal().slice(0, 7));
      const amt = (pm && !alreadyPaid && pm.remaining > 0) ? pm.remaining : (libreSig && libreSig.remaining > 0 ? libreSig.remaining : eff);
      const eMes = mesesA.find((x) => x.month === month);
      setAbonadoMes(eMes && eMes.remaining > 0 && eMes.expected > eMes.remaining ? Math.round((eMes.expected - eMes.remaining) * 100) / 100 : 0);
      const sug = {
        plan: mm.membership_name || '',
        disc: mm.discount_name ? `${mm.discount_name} (${discountLabel(mm.discount_value, mm.discount_is_percentage)})` : '',
        monthLabel: monthLabelEs(month),
        amount: amt,
        alreadyPaid,
      };
      // Su descuento se liga solo (si está vigente): el pago lo guarda y el desglose explica el monto.
      const descId = mm.discount_id && !mm.discount_expired ? String(mm.discount_id) : '';
      setDescAlumna(mm.discount_id ? Number(mm.discount_id) : null);
      setFamilia(Array.isArray(mm.posible_familia) ? mm.posible_familia : []);
      setFamiliaConf(Array.isArray(mm.familia) ? mm.familia : []);
      setPrecioLista(Number(mm.membership_price_month) || null);
      setMesesAlumna(Array.isArray(mm.meses) ? mm.meses : []);
      mensSug.current = { amount: amt, months: month, assoc: mm.membership_name || '', discountId: descId, suggest: sug };
      // Solo se APLICA si se está cobrando mensualidad; si ya eligieron otro tipo (clase suelta), se
      // guarda para cuando vuelvan a «Mensualidad» y no se les cambia el tipo por debajo.
      if (tipoRef.current !== 'membership') return;
      setAmount(String(amt));
      setAssoc(mm.membership_name || '');
      setMonths(month);
      setSuggest(sug);
      setDiscountId(descId);
    }).catch(() => setSuggest(null));
    return () => { cancel = true; };
  }, [picked?.account_id]);

  // Búsqueda con debounce
  useEffect(() => {
    if (picked || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      listMembers({ search: q.trim(), page_size: 8 })
        .then((r) => { setResults(r.data); setOpenList(true); })
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, picked]);

  // Cerrar la lista al hacer clic afuera
  useEffect(() => {
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpenList(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  /** Eliminar el pago que se está editando (reabre el adeudo si lo estaba saldando). */
  async function borrar() {
    if (!edit) return;
    if (!confirm(`¿Eliminar este pago de $${Number(amount).toLocaleString('es-MX')}?\n\nSi estaba saldando un adeudo, el adeudo se vuelve a abrir.`)) return;
    setBorrando(true);
    try {
      await deletePayment(edit.payment_id);
      toast.success('Pago eliminado');
      onCreated(payDate);
    } catch (err: any) { toast.error(err?.message || 'No se pudo eliminar el pago'); }
    finally { setBorrando(false); }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    const muestraGratis = type === 'voucher_trial' && amt === 0 && !edit && !abono;
    if (enDolares && !(Number(usd) > 0)) { toast.error('Indica cuántos dólares'); return; }
    if (!(amt > 0) && !muestraGratis) { toast.error('Indica un monto válido'); return; }
    // «Otro»: sin concepto nadie sabría después qué se cobró (en reportes sale en «Otros»).
    if (type === 'other' && !assoc.trim()) { toast.error('Escribe el concepto: ¿qué está pagando?'); return; }
    // Mensualidad sin mes (o mal escrito) = pago que Cobranza no cuenta en ningún mes. Al corregir un pago
    // viejo que ya venía sin mes (historial de Kydemy) no se exige, para no bloquear la corrección.
    const mesesOk = months.split(',').map((m) => m.trim()).filter(Boolean);
    if (type === 'membership' && (!edit || edit.months) && (!mesesOk.length || mesesOk.some((m) => !/^\d{4}-\d{2}$/.test(m)))) {
      toast.error('Indica el mes que paga la mensualidad (ej. ' + todayLocal().slice(0, 7) + ')'); return;
    }
    // ── EDICIÓN: PATCH de los campos capturables (el alumno del pago no se cambia aquí) ──
    if (edit) {
      setSaving(true);
      try {
        const r = await updatePayment(edit.payment_id, {
          type, method, amount: amt, pay_date: payDate,
          months: months || null, assoc_name: assoc || null, comment: comment || null,
          ...(type === 'other' && ingresoConcepto ? { concept_id: ingresoConcepto } : {}),
          // Dólares: se corrigen los USD; si le quitaron «Dólares», pasa a ser un cobro en pesos.
          ...(usdFijo ? (enDolares ? { usd_amount: Number(usd), mxn_amount: mxnNum || null } : { currency: 'MXN' as const }) : {}),
        });
        // OJO: el descuento NO se puede corregir desde aquí. El PATCH del backend sólo acepta
        // type/method/amount/pay_date/months/assoc_name/comment; `discount_id` se mandaba y el
        // servidor lo ignoraba en silencio (verificado 15-ago-2026). Para cambiar el descuento de
        // un pago hay que borrarlo y volver a capturarlo — o añadir soporte en el backend, que
        // implica recalcular base/IVA/descuento y por eso no se hace de paso.
        if (r.cashbox_closed) toast.info('Pago corregido · ojo: ya entró en un corte cerrado, y ese corte no se recalcula');
        else if (r.debt_settled === 1) toast.success('Pago corregido · el adeudo queda saldado');
        else if (r.debt_settled === 0) toast.success('Pago corregido · el adeudo vuelve a tener saldo');
        else toast.success('Pago corregido');
        onCreated(payDate);
      } catch (err: any) { toast.error(err?.message || 'No se pudo corregir el pago'); }
      finally { setSaving(false); }
      return;
    }
    if (abono && amt > abono.remaining + 0.009) { toast.error(`El abono no puede exceder el saldo ($${abono.remaining.toLocaleString('es-MX')})`); return; }
    if (!abono && !picked && !q.trim()) { toast.error('Selecciona un alumno o escribe quién paga'); return; }
    setSaving(true);
    try {
      const r = await createPayment({
        account_id: picked?.account_id ?? null,
        payer_name: picked ? null : q.trim(),
        type, method, amount: amt, pay_date: payDate,
        months: months || null, assoc_name: assoc || null, comment: comment || null,
        ...(type === 'other' && ingresoConcepto ? { concept_id: ingresoConcepto } : {}),
        ...(enDolares ? { currency: 'USD' as const, usd_amount: Number(usd), fx_rate: tc, mxn_amount: mxnNum || null } : {}),
        // Clase suelta con clase elegida → aparece en «Pasar lista» de esa clase ese día (F3).
        class_id: esClase && claseId && (type === 'class' || type === 'voucher_trial') ? claseId : null,
        // «Es un abono» se quitó del formulario (29-sep-2026): en mensualidad el resto lo lleva el cargo del
        // mes, y en lo demás el camino es «Registrar deuda» + abonar. El backend aún acepta is_partial.
        is_partial: false, total_expected: null,
        debt_id: abono ? abono.debt_id : null,
        // El importe del descuento lo recalcula el servidor desde el catálogo (amount = NETO);
        // aquí solo viaja el id. El selector solo existe cuando !edit && !abono.
        discount_id: !abono && discountId ? discountId : null,
      });
      // Descuento de familia aplicado a una posible familia → queda registrado el parentesco.
      const descFinal = discounts.find((d) => String(d.discount_id) === discountId);
      if (picked?.account_id && vincularCon.length && descFinal && Number(descFinal.is_family)) {
        for (const otra of vincularCon) await linkFamily(picked.account_id, otra).catch(() => {});
      }
      if (abono) {
        toast.success(r.debt_settled
          ? 'Abono registrado · adeudo SALDADO ✅'
          : `Abono registrado · resta $${Number(r.debt_remaining || 0).toLocaleString('es-MX')} del adeudo`);
      } else if (r.is_partial && r.remaining && r.remaining > 0) toast.success(`Abono registrado · resta $${r.remaining.toLocaleString('es-MX')} (se creó adeudo)`);
      else toast.success('Pago registrado');
      // Caja cerrada (1-oct-2026, audio de Mabel): el cobro no se pierde, lo toma la siguiente caja que se abra.
      if ((r as any).sin_caja && method === 'cash') toast.info('La caja está cerrada: este cobro en efectivo se sumará a la siguiente caja que se abra');
      onCreated(r.pay_date);
    } catch (err: any) {
      // El TC cambió mientras cobraban: se pone el nuevo para que vean el monto antes de guardar otra vez.
      if (err?.status === 409 && err?.data?.fx_rate) setTc(Number(err.data.fx_rate));
      toast.error(err?.message || 'No se pudo registrar el pago');
    } finally { setSaving(false); }
  }

  // Selector de meses: solo al cobrar MENSUALIDAD a una alumna con estado mes a mes (no al corregir ni abonar).
  const selectorMeses = type === 'membership' && !edit && !abono && mesesAlumna.length > 0;
  const mesesElegidos = months.split(',').map((m) => m.trim().slice(0, 7)).filter(Boolean);
  // Mensualidad con mes elegido: el ABONO es simplemente cobrar menos. El resto lo lleva el cargo del mes
  // (ya generado, o se genera el día 1 tomando este pago como anticipo). La casilla «Es un abono» creaba
  // además un adeudo «Resto de…» y el resto se cobraba dos veces (Carlos 29-sep-2026).
  const mensConMes = type === 'membership' && !edit && !abono && mesesElegidos.length > 0;
  const faltaMeses = mensConMes ? Math.round(mesesElegidos.reduce((t, m) => {
    const e = mesesAlumna.find((x) => x.month === m);
    if (!e) return t + (Number(mensSug.current?.amount) || 0);
    return t + (e.remaining > 0 ? e.remaining : e.status === 'pagado' ? 0 : e.expected);
  }, 0) * 100) / 100 : 0;
  const restoMens = mensConMes && faltaMeses > 0 && Number(amount) > 0 ? Math.round((faltaMeses - Number(amount)) * 100) / 100 : 0;
  const mesInicio = mesesElegidos[0] || '';
  const mesActual = todayLocal().slice(0, 7);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{edit ? 'Editar pago' : abono ? 'Abonar al adeudo' : 'Registrar pago'}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Contexto del abono a adeudo */}
          {abono && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-content-2">
              💳 Abono a: <b className="text-content">{abono.concept}</b> · saldo <b className="text-amber-600 dark:text-amber-400">${abono.remaining.toLocaleString('es-MX')}</b>
              <span className="text-content-3"> — al cubrirlo, el adeudo se salda solo.</span>
            </div>
          )}
          {/* Alumno / pagador — en edición no se cambia de dueño: se muestra y punto. */}
          {edit ? (
            picked && (
              <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
                <PersonAvatar contactId={picked.account_id} hasPhoto={picked.has_photo}
                  name={picked.firstname} lastName={picked.lastname}
                  size="h-7 w-7" ringClass="fill-surface-2" />
                <span className="flex-1 truncate text-sm text-content">{picked.firstname} {picked.lastname}</span>
              </div>
            )
          ) : (
          <div ref={boxRef} className="relative">
            <label className={labelCls}>Alumno o quien paga</label>
            {picked ? (
              <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
                <PersonAvatar contactId={picked.account_id} hasPhoto={picked.has_photo}
                  name={picked.firstname} lastName={picked.lastname}
                  size="h-7 w-7" ringClass="fill-surface-2" />
                <span className="flex-1 truncate text-sm text-content">{picked.firstname} {picked.lastname}</span>
                <button type="button" onClick={() => { setPicked(null); setQ(''); }} className="text-content-3 hover:text-content"><X size={15} /></button>
              </div>
            ) : (
              <div className="relative">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onFocus={() => results.length && setOpenList(true)}
                  placeholder="Buscar alumno por nombre… (o escribe un nombre libre)"
                  className={`${inputCls} pl-9`}
                />
                {openList && results.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
                    {results.map((m) => (
                      <button key={m.account_id} type="button"
                        onClick={() => { setPicked(m); setOpenList(false); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-surface-2">
                        <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
                          name={m.firstname} lastName={m.lastname}
                          size="h-6 w-6" initialClass="text-[10px]" />
                        <span className="truncate text-content">{m.firstname} {m.lastname}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!picked && <p className="mt-1 text-xs text-content-3">{searching ? 'Buscando…' : 'Si no es un alumno registrado, deja el nombre escrito tal cual.'}</p>}
          </div>
          )}

          {/* Beca (7-oct-2026): avisar antes de cobrar algo que la beca ya cubre. */}
          {beca && beca.vigente && ((type === 'membership' && beca.covers_membership) || (type === 'license' && becaInscPct > 0)) && (
            <div className="rounded-lg border border-violet-500/30 bg-violet-500/[0.06] px-3 py-2 text-xs text-content-2">
              🎓 <b className="text-content">Becado {beca.pct}%</b> hasta {new Date(`${beca.end_date}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
              {type === 'license'
                ? <> · la inscripción ya sale con la beca aplicada.</>
                : beca.pct >= 100 ? <> · no se le cobra mensualidad mientras dure la beca.</> : <> · su mensualidad baja {beca.pct}%.</>}
            </div>
          )}
          {/* Sugerencia según el plan del alumno */}
          {suggest && type === 'membership' && (
            <div className="rounded-lg border border-brand-blue/30 bg-brand-blue/5 px-3 py-2 text-xs text-content-2">
              💡 <b className="text-content">{suggest.plan || 'Mensualidad'}</b>
              {suggest.disc && <span className="text-brand-blue"> · {suggest.disc}</span>}
              {' · '}
              {suggest.alreadyPaid
                ? <>ya pagó este mes; sugerimos <b className="text-content">{suggest.monthLabel}</b></>
                : <>mensualidad de <b className="text-content">{suggest.monthLabel}</b></>}
              {': '}<b className="text-content">${suggest.amount.toLocaleString('es-MX')}</b>
              <span className="text-content-3"> — ya prellenado, puedes ajustarlo.</span>
            </div>
          )}

          {type === 'membership' && <PlanClasesAviso check={planCheck} />}

          {/* Tipo (cuadritos, como en Kydemy) */}
          <div>
            <label className={labelCls}>Tipo de pago</label>
            {/* 29-sep-2026 (Carlos): un solo cuadro «Clase suelta» («es clase suelta, no de prueba; me es muy
                confuso»). La muestra gratis (1ª clase de un estilo) la sigue detectando trial-check y la guarda
                como voucher_trial, pero en pantalla solo dice «muestra gratis». «Otro» (concepto libre) regresó el
                2-oct-2026; «Bono de clases» (nunca usado) sigue fuera: solo salen al corregir un pago viejo de ese tipo. */}
            <Tiles value={type === 'voucher_trial' ? 'class' : type}
              options={[...TIPOS_COMUNES, ...PAY_TYPE_OPTIONS.filter((o) => o.v === type && o.v !== 'voucher_trial' && !TIPOS_COMUNES.some((c) => c.v === o.v))]}
              onPick={elegirTipo} cols={TIPOS_COLS} />
          </div>

          {/* Vitrina: el carrito de la Caja, con la alumna ya elegida. La captura a mano de abajo
              queda solo para lo que no está en el catálogo. */}
          {type === 'product' && !edit && !abono && (
            <div className="flex items-center gap-2 rounded-lg border border-accent/50 bg-accent/10 px-3 py-2 text-xs text-content-2" data-testid="pago-vitrina-carrito">
              <ShoppingBag size={15} className="shrink-0 text-accent-ink" />
              <span className="min-w-0 flex-1">Elige los artículos y se registran desglosados, igual que en la Caja.</span>
              <button type="button" onClick={() => setVitrinaOpen(true)}
                className="shrink-0 rounded-md bg-accent-solid px-2.5 py-1 font-semibold text-on-accent transition hover:bg-accent-solid/90">
                Elegir artículos
              </button>
            </div>
          )}

          {/* Método (cuadritos) */}
          <div>
            <label className={labelCls}>Método</label>
            <Tiles value={enDolares ? 'cash_usd' : method} options={[...PAY_METHOD_OPTIONS, { v: 'cash_usd', label: 'Dólares' }]}
              onPick={(v) => { if (v === 'cash_usd') { setEnDolares(true); setMethod('cash'); } else { setEnDolares(false); setMethod(v); } }}
              cols="grid-cols-2 sm:grid-cols-4" conIcono />
          </div>

          {/* Dólares: cuántos USD × tipo de cambio = pesos (el monto de abajo se llena solo). */}
          {enDolares && (
            <div data-testid="bloque-dolares" className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="grid grid-cols-2 items-end gap-3">
                <div>
                  <label className={labelCls}>Dólares (USD)</label>
                  <input data-testid="usd-input" inputMode="decimal" value={usd} placeholder="0"
                    onChange={(e) => setUsd(e.target.value.replace(/[^\d.]/g, ''))} className={inputCls} />
                </div>
                <div className="pb-1 text-sm text-content-2">
                  × tipo de cambio <b data-testid="tc-valor" className="text-content">{tc}</b>
                  {puedeTc && !usdFijo && editTc == null && (
                    <button type="button" data-testid="tc-editar" onClick={() => setEditTc(String(tc))}
                      className="ml-1.5 text-xs text-brand-blue hover:underline">cambiar</button>
                  )}
                  <div data-testid="usd-pesos" className="font-semibold text-content">= ${pesosDeDolares(Number(usd) || 0, tc).toLocaleString('es-MX')} MXN</div>
                </div>
              </div>
              {editTc != null && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-content-2">
                  Nuevo tipo de cambio
                  <input data-testid="tc-input" inputMode="decimal" value={editTc} onChange={(e) => setEditTc(e.target.value.replace(/[^\d.]/g, ''))}
                    className="w-16 rounded border border-line bg-surface px-1.5 py-0.5 text-right" />
                  <button type="button" data-testid="tc-guardar" onClick={guardarTc} className="rounded bg-accent-solid px-2 py-0.5 font-semibold text-on-accent">Guardar</button>
                  <button type="button" onClick={() => setEditTc(null)} className="text-content-3 hover:text-content">Cancelar</button>
                  <span className="text-content-3">· cambia para todos los cobros y la caja</span>
                </div>
              )}
              {/* Cobro MIXTO: una parte en dólares y otra en pesos, en UN solo cobro (3-nov-2026). */}
              <div className="mt-3 grid grid-cols-2 items-end gap-3">
                <div>
                  <label className={labelCls}>+ Pesos en efectivo <span className="normal-case tracking-normal text-content-3">(si pagó una parte en pesos)</span></label>
                  <input data-testid="mxn-input" inputMode="decimal" value={mxn} placeholder="0"
                    onChange={(e) => setMxn(e.target.value.replace(/[^\d.]/g, ''))} className={inputCls} />
                </div>
                {mxnNum > 0 && (
                  <div data-testid="mixto-total" className="pb-1 text-sm text-content-2">
                    Total <b className="text-content">${(pesosDeDolares(Number(usd) || 0, tc) + mxnNum).toLocaleString('es-MX')} MXN</b>
                    <div className="text-xs text-content-3">{Number(usd) || 0} USD + ${mxnNum.toLocaleString('es-MX')} en pesos</div>
                  </div>
                )}
              </div>
              {usdFijo && <p className="mt-1 text-[11px] text-content-3">Se corrige con el tipo de cambio del día del cobro ({tc}).</p>}
            </div>
          )}

          {/* Monto + fecha */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                Monto (MXN)
                {esClase && check && !edit && !abono && (
                  <span className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal ${check.saturday ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300' : 'bg-sky-500/15 text-sky-700 dark:text-sky-300'}`}>
                    {check.charge === 0 ? 'muestra gratis' : check.saturday ? 'precio de sábado' : 'precio entre semana'}
                  </span>
                )}
              </label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={amount} disabled={enDolares}
                title={enDolares ? 'Sale de los dólares × tipo de cambio' : undefined}
                onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={`${inputCls} disabled:opacity-70`} />
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Descuento aplicado al cobro: queda guardado con el pago, sale en el recibo y explica
              solo por qué entró menos que la mensualidad. Solo al crear (no al editar/abonar). */}
          {!edit && !abono && opcionesDesc.length > 0 && (
            <div>
              <label className={labelCls}>Descuento (opcional)</label>
              {type === 'membership' && (familia.length > 0 || familiaConf.length > 0) && (
                <div className="mb-2 rounded-lg bg-brand-blue/5 px-2.5 py-2 text-xs text-content-2 ring-1 ring-brand-blue/15" data-testid="aviso-familia">
                  {familiaConf.length > 0 && (
                    <p>👪 <b className="text-content">Familia:</b>{' '}
                      {familiaConf.map((f, i) => <span key={f.contact_id}>{i > 0 && ' · '}{f.nombre}{!f.activa && <span className="text-red-600 dark:text-red-400"> (baja)</span>}</span>)}</p>
                  )}
                  {familia.length > 0 && (
                    <p>👪 <b className="text-content">Posible familia:</b>{' '}
                      {familia.map((f, i) => <span key={f.contact_id}>{i > 0 && ' · '}{f.nombre} <span className="text-content-3">({f.razones.join(' y ')})</span></span>)}</p>
                  )}
                  {descFamiliaPropio ? (
                    // Ya tiene SU descuento de familia en la ficha: no se ofrecen otros (Alika: salían 3 y confundía).
                    <p className="mt-1">Su descuento de familia: <b className="text-content">{descFamiliaPropio.name} ({discountLabel(descFamiliaPropio.value, descFamiliaPropio.is_percentage)})</b>, el de su ficha.</p>
                  ) : descFamilia.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="text-content-3">Descuentos de familia:</span>
                      {descFamilia.map((d) => (
                        <button key={d.discount_id} type="button" onClick={() => aplicarDescFamilia(d)}
                          className={`rounded-full border px-2 py-0.5 font-medium transition ${String(d.discount_id) === discountId ? 'border-transparent bg-accent-solid text-on-accent' : 'border-line bg-surface text-content-2 hover:text-content'}`}>
                          {d.name} ({discountLabel(d.value, d.is_percentage)}){d.deleted_at ? ' · archivado' : ''}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="mt-1 text-[11px] text-content-3">
                    {descFamiliaPropio ? 'Si ya no le corresponde, cámbialo en el selector de abajo o en su ficha.'
                      : vincularCon.length && Number(descElegido?.is_family)
                      ? <>Al registrar el pago quedará anotada como familia de <b>{familia.filter((f) => vincularCon.includes(f.contact_id)).map((f) => f.nombre).join(', ')}</b>.</>
                      : 'Es solo una sugerencia: no todas las familias tienen descuento.'}
                  </p>
                </div>
              )}
              <select value={discountId} onChange={(e) => elegirDescuento(e.target.value)} className={inputCls}>
                <option value="">— Sin descuento —</option>
                {((familia.length > 0 || familiaConf.length > 0) && type === 'membership'
                  ? [...opcionesDesc].sort((a, b) => Number(b.is_family || 0) - Number(a.is_family || 0))
                  : opcionesDesc).map((d) => (
                  <option key={d.discount_id} value={d.discount_id}>
                    {d.discount_id === descAlumna ? '★ ' : ''}{d.name} ({discountLabel(d.value, d.is_percentage)}){d.deleted_at ? ' · archivado' : ''}{Number(d.included_in_price) ? ' · incluido en el plan' : ''}
                  </option>
                ))}
              </select>
              {descElegido && Number(descElegido.discount_id) === descAlumna && (
                <p className="mt-1 text-[11px] text-content-3">★ Es el descuento de su ficha: se puso solo.</p>
              )}
              {descElegido && Number(amount) > 0 && Number(descElegido.included_in_price) ? (
                <p className="mt-1 text-xs text-content-3">
                  Ya viene <b className="text-content">incluido en el precio del plan</b>: cobras <b className="text-content">${Number(amount).toLocaleString('es-MX')}</b>
                  {' '}(sin él serían ${precioSinDesc.toLocaleString('es-MX')}). No se resta otra vez.
                </p>
              ) : descElegido && Number(amount) > 0 && (
                <p className="mt-1 text-xs text-content-3">
                  Cobras <b className="text-content">${Number(amount).toLocaleString('es-MX')}</b> ·
                  descuento <b className="text-amber-600 dark:text-amber-400">−${descImporte.toLocaleString('es-MX')}</b> ·
                  precio sin descuento <b className="text-content">${precioSinDesc.toLocaleString('es-MX')}</b>
                </p>
              )}
            </div>
          )}

          {/* Clase suelta / muestra: elegir la clase y ver qué toca cobrar */}
          {esClase && !edit && !abono && (
            <div className="rounded-xl border border-line bg-surface-2/50 p-3">
              <label className={labelCls}>Clase</label>
              <select value={claseId ?? ''} onChange={(e) => setClaseId(e.target.value ? Number(e.target.value) : null)} className={inputCls}>
                <option value="">— Elige la clase —</option>
                {clases.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}{c.style && c.style !== c.name ? ` · ${c.style}` : ''}</option>)}
              </select>
              {check && (
                <div className="mt-2 text-xs">
                  {check.reason === 'muestra_gratis' && (
                    <p className="rounded-lg bg-emerald-500/10 px-2.5 py-2 text-emerald-700 dark:text-emerald-400">
                      ✅ <b>Muestra gratis.</b> Es alumno, no está inscrito en <b>{check.style}</b> y no ha usado su muestra ahí. Se registra en $0 para que cuente (una por estilo, de por vida).
                    </p>
                  )}
                  {check.reason === 'muestra_usada' && (
                    <p className="rounded-lg bg-amber-500/10 px-2.5 py-2 text-amber-700 dark:text-amber-400">
                      ⚠️ Ya usó su muestra gratis de <b>{check.style}</b>{check.muestra_used?.date ? ` el ${check.muestra_used.date}` : ''}. Toca <b>clase suelta ${check.price}</b> ({check.saturday ? 'sábado' : 'entre semana'}).
                    </p>
                  )}
                  {check.reason === 'inscrito_en_estilo' && (
                    <p className="rounded-lg bg-amber-500/10 px-2.5 py-2 text-amber-700 dark:text-amber-400">
                      ⚠️ Ya está inscrito en <b>{check.style}</b>: no aplica muestra. Toca <b>clase suelta ${check.price}</b> ({check.saturday ? 'sábado' : 'entre semana'}).
                    </p>
                  )}
                  {(check.reason === 'externo' || check.reason === 'inactivo' || check.reason === 'sin_clase_vigente') && (
                    <p className="rounded-lg bg-surface px-2.5 py-2 text-content-2 ring-1 ring-line">
                      {check.reason === 'externo' ? 'No es alumno' : check.reason === 'inactivo' ? 'Alumno dado de baja' : 'Alumno sin clase vigente'}: la muestra gratis es solo para alumnos. Toca <b>clase suelta ${check.price}</b> ({check.saturday ? 'sábado' : 'entre semana'}).
                    </p>
                  )}
                  {check.reason === 'sin_clase' && <p className="text-content-3">Elige la clase para saber si le toca muestra gratis o clase suelta.</p>}
                  <div className="mt-1.5 flex items-center justify-between text-[11px] text-content-3">
                    <span className="flex flex-wrap items-center gap-1.5">
                      Clase suelta:
                      <span className={`rounded px-1.5 py-0.5 ${!check.saturday ? 'bg-sky-500/15 font-semibold text-sky-700 dark:text-sky-300' : ''}`}>entre semana ${check.prices.weekday}</span>
                      <span className={`rounded px-1.5 py-0.5 ${check.saturday ? 'bg-violet-500/15 font-semibold text-violet-700 dark:text-violet-300' : ''}`}>sábado ${check.prices.saturday}</span>
                      <span>· {check.saturday_por === 'clase' ? 'la clase es en sábado' : check.saturday_por === 'clase_semana' ? 'la clase es entre semana' : check.saturday ? 'hoy es sábado' : 'por la fecha del pago'}</span>
                    </span>
                    {!editPrices
                      ? <button type="button" onClick={() => setEditPrices({ weekday: String(check.prices.weekday), saturday: String(check.prices.saturday) })} className="text-brand-blue hover:underline">editar precios</button>
                      : <span className="flex items-center gap-1">
                          sem $<input type="number" value={editPrices.weekday} onChange={(e) => setEditPrices({ ...editPrices, weekday: e.target.value })} className="w-16 rounded border border-line bg-surface px-1 py-0.5 text-right" />
                          sáb $<input type="number" value={editPrices.saturday} onChange={(e) => setEditPrices({ ...editPrices, saturday: e.target.value })} className="w-16 rounded border border-line bg-surface px-1 py-0.5 text-right" />
                          <button type="button" onClick={guardarPrecios} className="rounded bg-accent px-2 py-0.5 font-semibold text-on-accent">OK</button>
                        </span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* «Otro»: en qué renglón de ingresos cae (catálogo de Mabel, 7-oct-2026). */}
          {type === 'other' && (
            <div>
              <label className={labelCls}>Renglón de ingresos</label>
              <ConceptoSelect value={ingresoConcepto} onChange={(id) => setIngresoConcepto(id)} secciones={['ingreso']}
                placeholder="Automático (A&B, merch o «Otros ingresos»)" className={inputCls} />
            </div>
          )}
          {/* Concepto + meses */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Concepto</label>
              <input data-testid="pago-concepto" value={assoc} onChange={(e) => setAssoc(e.target.value)}
                placeholder={type === 'other' ? '¿Qué paga? Ej. deuda de Kydemy' : 'Ej. F+60, evento…'}
                className={`${inputCls}${type === 'other' && !assoc.trim() ? ' border-accent' : ''}`} />
            </div>
            {!selectorMeses && (
            <div>
              <label className={labelCls}>{type === 'membership' ? 'Mes que paga' : 'Meses (opcional)'}</label>
              <input data-testid="meses-texto" value={months} onChange={(e) => setMonths(e.target.value)} placeholder={`Ej. ${todayLocal().slice(0, 7)}`} className={inputCls} />
            </div>
            )}
            {selectorMeses && (
            <div>
              <label className={labelCls}>Cuántos meses</label>
              <select data-testid="meses-cuantos" value={nMeses} onChange={(e) => elegirMeses(mesInicio, Number(e.target.value))} className={inputCls}>
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n === 1 ? '1 mes' : `${n} meses (curso)`}</option>)}
              </select>
            </div>
            )}
          </div>

          {/* Mensualidad: el mes se ELIGE (23-sep-2026, Carlos: «¿no podría ser un drop down?»). Los meses de
              atrás ya salen saldados y un mes que todavía no se cobra se marca como ADELANTO. */}
          {selectorMeses && (
            <div>
              <label className={labelCls}>Mes que paga</label>
              <select data-testid="meses-select" value={mesInicio} onChange={(e) => elegirMeses(e.target.value, nMeses)} className={inputCls}>
                {!mesesAlumna.some((e) => e.month === mesInicio) && mesInicio && <option value={mesInicio}>{monthLabelEs(mesInicio)}</option>}
                {mesesAlumna.map((e) => (
                  <option key={e.month} value={e.month} disabled={e.status === 'pagado' || e.status === 'sin_cargo'}>{textoMes(e)}</option>
                ))}
              </select>
              <div data-testid="meses-historial" className="mt-1.5 flex flex-wrap gap-1 text-[11px]">
                {mesesAlumna.filter((e) => !e.futuro).map((e) => (
                  <span key={e.month} title={textoMes(e)}
                    className={'rounded px-1.5 py-0.5 ' + (e.status === 'pagado' ? 'bg-emerald-500/10 text-emerald-600'
                      : e.status === 'sin_cargo' ? 'bg-surface-2 text-content-3'
                      : 'bg-amber-500/10 text-amber-600')}>
                    {mesCorto(e.month)} {e.status === 'pagado' ? '✓' : e.status === 'sin_cargo' ? '—' : '•'}
                  </span>
                ))}
              </div>
              {restoMens > 0.009 && (
                <p data-testid="mens-abono" className="mt-1.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-2.5 py-1.5 text-xs text-content-2">
                  💳 <b className="text-content">Abono</b>: paga ${Number(amount).toLocaleString('es-MX')} de ${faltaMeses.toLocaleString('es-MX')} ·
                  quedan <b className="text-amber-600 dark:text-amber-400">${restoMens.toLocaleString('es-MX')}</b> pendientes de {mesesElegidos.map(monthLabelEs).join(', ')}
                  {mesesElegidos.every((m) => m > mesActual) ? ' (se le cobran cuando se genere ese mes).' : ' en su adeudo del mes.'}
                </p>
              )}
              {mesesElegidos.some((m) => m > mesActual) && restoMens <= 0.009 && (
                <p data-testid="meses-adelanto" className="mt-1.5 rounded-lg border border-brand-blue/30 bg-brand-blue/5 px-2.5 py-1.5 text-xs text-content-2">
                  ⏩ <b className="text-content">Adelanto</b> de {mesesElegidos.filter((m) => m > mesActual).map(monthLabelEs).join(', ')}:
                  queda a cuenta y, cuando se generen esas mensualidades, este pago las cubre (no le sale adeudo ni pago tardío).
                </p>
              )}
              {mesesElegidos.some((m) => mesesAlumna.find((e) => e.month === m)?.status === 'pagado') && (
                <p className="mt-1.5 text-xs text-amber-600">⚠️ Alguno de esos meses ya está saldado: revisa si en realidad es otro mes.</p>
              )}
            </div>
          )}

          {/* Comentario */}
          <div>
            <label className={labelCls}>Comentario (opcional)</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} className={inputCls} />
          </div>

          {/* Sin casilla «Es un abono» (Carlos 29-sep-2026: «me sigue causando ruido»; se usó 1 vez en toda la
              historia). Pagar una parte: mensualidad → poner el monto (el resto queda en el cargo del mes);
              otro concepto → «Registrar deuda» en la ficha y abonar ahí. */}

          <div className="flex items-center justify-between gap-3 pt-1">
            {/* Eliminar pago: solo al corregir uno existente (a la izquierda, como en Kydemy) */}
            {edit ? (
              <button type="button" onClick={borrar} disabled={borrando || saving}
                className="rounded-lg border border-red-500/40 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400">
                {borrando ? 'Eliminando…' : 'Eliminar pago'}
              </button>
            ) : <span />}
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
              <button type="submit" disabled={saving || borrando}
                className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                {saving ? 'Guardando…' : edit ? 'Guardar cambios' : 'Registrar pago'}
              </button>
            </div>
          </div>
        </form>
      </div>
      {vitrinaOpen && (
        <VentaVitrinaModal alumnaInicial={picked} onClose={() => setVitrinaOpen(false)}
          onSold={() => { setVitrinaOpen(false); onCreated(todayLocal()); }} />
      )}
    </div>,
    document.body
  );
}
